export enum EditorNodeKind {
  MAP_AND_COLORS = 'MAP_AND_COLORS',
  SPOT = 'SPOT',
  HOTSPOT = 'HOTSPOT',
  REGION_SCRIPT = 'REGION_SCRIPT',
  MAP_LAYOUT = 'MAP_LAYOUT',
  MAP_SEGMENT = 'MAP_SEGMENT',
  BRANDING_ENTITY = 'BRANDING_ENTITY',
  REALTIME_ENTITY = 'REALTIME_ENTITY',
}

export enum EditorGroupId {
  ENVIRONMENT = 'ENVIRONMENT',
  SPOTS = 'SPOTS',
  HOTSPOTS = 'HOTSPOTS',
  AI_SCRIPTS = 'AI_SCRIPTS',
  MAP_EDITOR = 'MAP_EDITOR',
  OBJECTS = 'OBJECTS',
  ZONES = 'ZONES',
  // Entities spawned live over the WebSocket — distinct from
  // OBJECTS, which is the persisted branding-entity theming group.
  REALTIME_OBJECTS = 'REALTIME_OBJECTS',
}

export type EditorGroupStatus = 'available' | 'coming_soon' | 'hidden';

export interface EditorGroupConfig {
  id: EditorGroupId;
  labelKey: string;
  labelFallback: string;
  status: EditorGroupStatus;
  nodeKind?: EditorNodeKind;
}

export const EDITOR_GROUPS: EditorGroupConfig[] = [
  {
    id: EditorGroupId.ENVIRONMENT,
    labelKey: 'editorMode.groupEnvironment',
    labelFallback: 'Environment',
    status: 'available',
    nodeKind: EditorNodeKind.MAP_AND_COLORS,
  },
  {
    id: EditorGroupId.SPOTS,
    labelKey: 'editorMode.groupSpots',
    labelFallback: 'Spots',
    status: 'available',
    nodeKind: EditorNodeKind.SPOT,
  },
  {
    id: EditorGroupId.HOTSPOTS,
    labelKey: 'editorMode.groupHotspots',
    labelFallback: 'Hotspots',
    status: 'available',
    nodeKind: EditorNodeKind.HOTSPOT,
  },
  {
    id: EditorGroupId.AI_SCRIPTS,
    labelKey: 'editorMode.groupAiScripts',
    labelFallback: 'AI scripts',
    status: 'available',
    nodeKind: EditorNodeKind.REGION_SCRIPT,
  },
  {
    id: EditorGroupId.MAP_EDITOR,
    labelKey: 'editorMode.groupMapEditor',
    labelFallback: 'Map editor',
    status: 'available',
    nodeKind: EditorNodeKind.MAP_LAYOUT,
  },
  {
    id: EditorGroupId.OBJECTS,
    labelKey: 'editorMode.groupObjects',
    labelFallback: 'Objects',
    status: 'available',
    nodeKind: EditorNodeKind.BRANDING_ENTITY,
  },
  {
    id: EditorGroupId.REALTIME_OBJECTS,
    labelKey: 'editorMode.groupSpawnedObjects',
    labelFallback: 'Spawned objects',
    status: 'available',
    nodeKind: EditorNodeKind.REALTIME_ENTITY,
  },
];

export type EditorFocus =
  | 'environment'
  | 'spots'
  | 'hotspots'
  | 'spawnables'
  | 'assistant'
  | 'mapEditor'
  | 'objects';

export interface EditorFocusConfig {
  id: EditorFocus;
  labelKey: string;
  labelFallback: string;
  descriptionKey: string;
  descriptionFallback: string;
  status: 'available' | 'coming_soon';
}

export const EDITOR_FOCUSES: EditorFocusConfig[] = [
  {
    id: 'environment',
    labelKey: 'editorMode.focusEnvironment',
    labelFallback: 'Map & colors',
    descriptionKey: 'editorMode.focusEnvironmentDesc',
    descriptionFallback: 'Theme, palette and environment art',
    status: 'available',
  },
  {
    id: 'spots',
    labelKey: 'editorMode.focusSpots',
    labelFallback: 'Spots & media',
    descriptionKey: 'editorMode.focusSpotsDesc',
    descriptionFallback: 'Images, videos and QR placements',
    status: 'available',
  },
  {
    id: 'hotspots',
    labelKey: 'editorMode.focusHotspots',
    labelFallback: 'Hotspots',
    descriptionKey: 'editorMode.focusHotspotsDesc',
    descriptionFallback: 'Interactive 2D panels & 3D objects',
    status: 'available',
  },
  {
    id: 'spawnables',
    labelKey: 'editorMode.focusSpawnables',
    labelFallback: 'Spawned objects',
    descriptionKey: 'editorMode.focusSpawnablesDesc',
    descriptionFallback: 'Live-spawned engine props',
    status: 'available',
  },
  {
    id: 'assistant',
    labelKey: 'editorMode.focusAssistant',
    labelFallback: 'AI scripts & context',
    descriptionKey: 'editorMode.focusAssistantDesc',
    descriptionFallback: 'Guided tours and ask-me-anything',
    status: 'available',
  },
  {
    id: 'mapEditor',
    labelKey: 'editorMode.focusMapEditor',
    labelFallback: 'Map editor',
    descriptionKey: 'editorMode.focusMapEditorDesc',
    descriptionFallback: 'Build the floor plan from room elements',
    status: 'available',
  },
  {
    id: 'objects',
    labelKey: 'editorMode.focusObjects',
    labelFallback: 'Objects',
    descriptionKey: 'editorMode.focusObjectsDesc',
    descriptionFallback: 'Place and theme 3D props',
    status: 'available',
  },
];

export const EDITOR_FOCUSES_COMING_SOON: EditorFocusConfig[] = [
  {
    id: 'environment',
    labelKey: 'editorMode.focusZones',
    labelFallback: 'Zones',
    descriptionKey: 'editorMode.focusZonesDesc',
    descriptionFallback: 'Define triggers and play areas',
    status: 'coming_soon',
  },
];

export const FOCUS_TO_GROUP_IDS: Record<EditorFocus, EditorGroupId[]> = {
  environment: [EditorGroupId.ENVIRONMENT],
  spots: [EditorGroupId.SPOTS],
  hotspots: [EditorGroupId.HOTSPOTS],
  spawnables: [EditorGroupId.REALTIME_OBJECTS],
  assistant: [EditorGroupId.AI_SCRIPTS],
  mapEditor: [EditorGroupId.MAP_EDITOR],
  objects: [EditorGroupId.OBJECTS],
};

export type EditorSelectedNode =
  | { kind: EditorNodeKind.MAP_AND_COLORS }
  | { kind: EditorNodeKind.SPOT; ueIdentifier: string }
  | { kind: EditorNodeKind.HOTSPOT; hotspotId: string }
  | {
      kind: EditorNodeKind.REGION_SCRIPT;
      trigger: string;
      scriptId: string | null;
    }
  | { kind: EditorNodeKind.MAP_LAYOUT }
  // One segment of the walk. `roomKey` is the placement key, and the entrance stands under
  // START_ROOM_KEY — the same key it stands under in Unreal, rather than a second vocabulary for
  // "the first row". A minted key is `r`-prefixed and a restored one `i`-prefixed, so neither can
  // collide with it.
  | { kind: EditorNodeKind.MAP_SEGMENT; roomKey: string }
  | { kind: EditorNodeKind.BRANDING_ENTITY; entityId: string }
  | { kind: EditorNodeKind.REALTIME_ENTITY; entityId: string }
  | null;

export type SpotType = 'IMAGE' | 'VIDEO' | 'QR';

export const getSpotType = (spot: unknown): SpotType => {
  const raw =
    typeof (spot as { type?: unknown })?.type === 'string'
      ? ((spot as { type: string }).type as string).toUpperCase()
      : '';
  if (raw === 'VIDEO') {
    return 'VIDEO';
  }
  if (raw === 'QR') {
    return 'QR';
  }

  return 'IMAGE';
};
